// src/services/protocolVersionService.js
//
// Protocol versions (2026-10-22): reads and writes for Version 1, 2, 3.
//   - Every protocol upload (study page or SourceX) asks "Is this a new version?"
//     when the study already has a protocol (NewVersionDialog).
//   - An admin's yes: the addProtocolVersion function makes it the current
//     version and compares it against the one before.
//   - Anyone else's yes: a request an admin approves (that same function) or
//     declines on the study page.
//   - No: a re-upload of the current version; nothing else happens.
// Rules in protocolVersions.js (pure) and functions/protocolVersionPlan.js.

import { db, auth } from "../firebase";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import { addDoc, collection, doc, onSnapshot, orderBy, query, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { getActiveTenantId, getActiveSiteId } from "./activeTenant";
import { getProtocolVersionsPath, getProtocolVersionRequestsPath, getProtocolEventsPath } from "./firestorePaths";

const me = () => String(auth.currentUser?.email || "").toLowerCase();

const callFn = async (name, body) => {
  const user = auth.currentUser;
  if (!user) throw new Error("You must be signed in.");
  const idToken = await user.getIdToken();
  const resp = await fetch(`${FUNCTIONS_BASE}/${name}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
    body: JSON.stringify(body || {}),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.error || `${name} failed (${resp.status}).`);
  return data;
};

const listen = (path, onData, onError) =>
  onSnapshot(
    collection(db, path),
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => onError && onError(err)
  );

/** Live list of the study's versions. */
export const listenVersions = (studyKey, onData, onError) => listen(getProtocolVersionsPath(studyKey), onData, onError);

/** Live list of requests for a new version (pending, approved, declined). */
export const listenVersionRequests = (studyKey, onData, onError) => listen(getProtocolVersionRequestsPath(studyKey), onData, onError);

/**
 * Admins: make an uploaded protocol the next version (and compare it against the
 * one before). Settles when the comparison is done; pages follow the docs.
 * @param {{studyKey:string, protocolId:string, source:"study-page"|"screening-card"|"sourcex", requestId?:string}} p
 */
export const addVersion = ({ studyKey, protocolId, source, requestId }) =>
  callFn("addProtocolVersion", { tenantId: getActiveTenantId(), siteId: getActiveSiteId(), studyKey, protocolId, source, ...(requestId ? { requestId } : {}) });

/** Not an admin: ask an admin to approve this upload as a new version. */
export const requestVersion = async ({ studyKey, protocolId, fileName, source }) => {
  const r = await addDoc(collection(db, getProtocolVersionRequestsPath(studyKey)), {
    protocolId,
    fileName: String(fileName || "").slice(0, 200),
    source,
    requestedBy: me(),
    requestedAt: serverTimestamp(),
    status: "pending",
  });
  logProtocolEvent(studyKey, { type: "request_sent", protocolId, detail: String(fileName || "") });
  return r;
};

/** Admin: "Not a new version". The upload stays as a re-upload. */
export const declineVersionRequest = async (studyKey, requestId, request = {}) => {
  await updateDoc(doc(db, getProtocolVersionRequestsPath(studyKey), requestId), { status: "declined", decidedBy: me(), decidedAt: serverTimestamp() });
  logProtocolEvent(studyKey, { type: "request_declined", protocolId: request.protocolId, detail: `${request.fileName || ""}${request.requestedBy ? ` (sent by ${request.requestedBy})` : ""}` });
};

/**
 * After an upload, the answer to "Is this a new version?". Yes from an admin
 * adds the version (the returned promise settles when its comparison is done);
 * yes from anyone else sends the request. No does nothing.
 */
export const answerNewVersion = ({ yes, isAdmin, studyKey, protocolId, fileName, source }) => {
  if (!yes) return Promise.resolve({ kind: "same" });
  if (isAdmin) return addVersion({ studyKey, protocolId, source }).then((r) => ({ kind: "added", ...r }));
  return requestVersion({ studyKey, protocolId, fileName, source }).then(() => ({ kind: "requested" }));
};

// ---- The study's protocol history (append-only) ----

/**
 * Add a line to the study's protocol history. Fail-soft, like the patient and
 * template audit trails: a lost history line must never break the action.
 * @param {string} studyKey
 * @param {{type:string, version?:number, protocolId?:string, visit?:string, detail?:string}} e
 */
export const logProtocolEvent = (studyKey, e) =>
  addDoc(collection(db, getProtocolEventsPath(studyKey)), {
    type: e.type,
    version: e.version ?? null,
    protocolId: e.protocolId ?? null,
    visit: e.visit ?? null,
    detail: String(e.detail || "").slice(0, 300),
    by: me(),
    at: serverTimestamp(),
  }).catch((err) => {
    // eslint-disable-next-line no-console
    console.warn("[protocolEvents] not recorded:", err && err.message);
  });

/** Live history, oldest first. */
export const listenProtocolEvents = (studyKey, onData, onError) =>
  onSnapshot(
    query(collection(db, getProtocolEventsPath(studyKey)), orderBy("at", "asc")),
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => onError && onError(err)
  );

// ---- Per-visit checks in SourceX's built source documents ----

const visitKey = (visit) => encodeURIComponent(String(visit || "")).slice(0, 700) || "_";

/** Live list of the visits checked against this version. */
export const listenVisitChecks = (studyKey, version, onData, onError) =>
  onSnapshot(
    collection(db, getProtocolVersionsPath(studyKey), String(version), "visitChecks"),
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => onError && onError(err)
  );

/** "I've checked this visit against Version N" (once per visit per version). */
export const checkVisit = async (studyKey, version, visit) => {
  await setDoc(doc(db, getProtocolVersionsPath(studyKey), String(version), "visitChecks", visitKey(visit)), {
    visit: String(visit || ""),
    version: Number(version),
    checkedBy: me(),
    checkedAt: serverTimestamp(),
  });
  logProtocolEvent(studyKey, { type: "visit_checked", version: Number(version), visit: String(visit || "") });
};

/**
 * Admins: "Compare Version N with Version N-1" (redline, then the AI summary
 * when switched on). Nothing compares on its own. Settles when it's done; the
 * page follows the version doc (its comparisonId) and the comparison doc.
 */
export const compareVersion = (studyKey, version) =>
  callFn("compareProtocolVersion", { tenantId: getActiveTenantId(), siteId: getActiveSiteId(), studyKey, version });
